/**
 * Vardiya servisi — haftalık planlama ve atama.
 * Firestore 'vardiyalar' koleksiyonu.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  writeBatch,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import { tarihStr, pazartesi } from '@/lib/tarih'
import { cokluBildirimGonder } from './bildirim-service'
import type { FsTimestamp } from '@/types'

const VARDIYALAR = 'vardiyalar'

// 'devamsiz' = noShowCron tarafından 3 saat sonra otomatik atanır (KARAR A2, spec 6.8).
// Maaş kesintisi OTOMATİK YAPILMAZ — BOSS panelden karar verir
// (Onayla / İzin say / Mazeret).
export type VardiyaDurumu = 'planli' | 'iptal' | 'izinli' | 'devamsiz'

// Devamsız vardiya için BOSS kararı.
export type DevamsizKararTipi = 'onaylandi' | 'izinSayildi' | 'mazeret'

export interface Vardiya {
  id: string
  personelId: string
  subeId: string
  tarih: string
  baslangic: string
  bitis: string
  kalipId?: string
  pozisyonId?: string
  durum: VardiyaDurumu
  notlar?: string
  haftaBaslangic: string
  yayinlandi: boolean
  olusturanId: string
  olusturmaTarihi: FsTimestamp
  guncellenmeTarihi: FsTimestamp

  // Devamsız karar alanları (BOSS karar verince doldurulur)
  devamsizKarari?: DevamsizKararTipi
  devamsizKararTarihi?: FsTimestamp
  devamsizKararVerenId?: string
  // İzin say durumunda izin kaynağı (yıllık/aylık/maas)
  devamsizIzinKaynagi?: 'yillik' | 'aylik' | 'maas'
  // İzin say durumunda oluşturulan izin talebi id'si (referans için)
  devamsizIzinTalepId?: string
  // Onayla durumunda oluşturulan puantaj id'si (referans için)
  devamsizPuantajId?: string
}

export interface VardiyaOlusturmaData {
  personelId: string
  subeId: string
  tarih: string
  baslangic: string
  bitis: string
  kalipId?: string
  pozisyonId?: string
  durum?: VardiyaDurumu
  notlar?: string
  yayinlandi?: boolean
}

function yeniId(): string {
  const db = getFirestoreDb()
  return doc(collection(db, VARDIYALAR)).id
}

export async function haftaVardiyalariGetir(
  haftaBaslangic: string,
  subeId: string
): Promise<Vardiya[]> {
  const db = getFirestoreDb()
  // Tek where + client filter — composite index gerektirmez
  const q = query(
    collection(db, VARDIYALAR),
    where('haftaBaslangic', '==', haftaBaslangic)
  )
  const snap = await getDocs(q)
  const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Vardiya)
  return liste.filter((v) => v.subeId === subeId)
}

/**
 * Personelin yayınlanmış vardiyalarını getir (bu hafta + gelecek haftalar).
 */
export async function personelGelecekVardiyalariGetir(
  personelId: string
): Promise<Vardiya[]> {
  const db = getFirestoreDb()
  const buHaftaStr = tarihStr(pazartesi(new Date()))

  // Tek where, geri kalanı client-side filtre
  const q = query(
    collection(db, VARDIYALAR),
    where('personelId', '==', personelId)
  )
  const snap = await getDocs(q)
  const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Vardiya)

  return liste
    .filter((v) => v.yayinlandi && v.haftaBaslangic >= buHaftaStr)
    .sort((a, b) => a.tarih.localeCompare(b.tarih))
}

/**
 * Bir personelin belirli bir tarihteki vardiyaları (giriş-çıkış geç/erken tespiti için).
 * Sadece yayınlanmış + planlı vardiyalar döner.
 */
export async function personelGununkuVardiyalari(
  personelId: string,
  tarih: string
): Promise<Vardiya[]> {
  const db = getFirestoreDb()
  const q = query(
    collection(db, VARDIYALAR),
    where('personelId', '==', personelId),
    where('tarih', '==', tarih)
  )
  const snap = await getDocs(q)
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as Vardiya)
    .filter((v) => v.yayinlandi)
}

/**
 * Karar bekleyen devamsız vardiyalar (BOSS feed kartı için).
 * durum='devamsiz' VE devamsizKarari yok.
 */
export async function bekleyenDevamsizVardiyalar(): Promise<Vardiya[]> {
  const db = getFirestoreDb()
  const q = query(
    collection(db, VARDIYALAR),
    where('durum', '==', 'devamsiz')
  )
  const snap = await getDocs(q)
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as Vardiya)
    .filter((v) => !v.devamsizKarari)
    .sort((a, b) => b.tarih.localeCompare(a.tarih))
}

export async function vardiyaGetir(id: string): Promise<Vardiya | null> {
  const db = getFirestoreDb()
  const d = await getDoc(doc(db, VARDIYALAR, id))
  if (!d.exists()) return null
  return { id: d.id, ...d.data() } as Vardiya
}

export async function vardiyaOlustur(
  data: VardiyaOlusturmaData,
  olusturanId: string
): Promise<string> {
  const db = getFirestoreDb()
  const id = yeniId()
  const haftaBaslangic = tarihStr(pazartesi(data.tarih))

  const docData: Record<string, unknown> = {
    personelId: data.personelId,
    subeId: data.subeId,
    tarih: data.tarih,
    baslangic: data.baslangic,
    bitis: data.bitis,
    durum: data.durum ?? 'planli',
    haftaBaslangic,
    yayinlandi: data.yayinlandi ?? false,
    olusturanId,
    olusturmaTarihi: nowServer(),
    guncellenmeTarihi: nowServer(),
  }
  if (data.kalipId) docData.kalipId = data.kalipId
  if (data.pozisyonId) docData.pozisyonId = data.pozisyonId
  if (data.notlar?.trim()) docData.notlar = data.notlar.trim()

  await setDoc(doc(db, VARDIYALAR, id), docData)
  return id
}

export async function vardiyaGuncelle(
  id: string,
  data: Partial<VardiyaOlusturmaData>
): Promise<void> {
  const db = getFirestoreDb()
  const update: Record<string, unknown> = { guncellenmeTarihi: nowServer() }
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined) return
    update[key] = typeof value === 'string' ? value.trim() : value
  })
  if (data.tarih) {
    update.haftaBaslangic = tarihStr(pazartesi(data.tarih))
  }
  await updateDoc(doc(db, VARDIYALAR, id), update)
}

export async function vardiyaSil(id: string): Promise<void> {
  const db = getFirestoreDb()
  await deleteDoc(doc(db, VARDIYALAR, id))
}

/**
 * Ayrılan personelin ÇALIŞILMAMIŞ planlı vardiyalarını iptal eder (SİLMEZ).
 *  - Çalışılmamış = o günün tarihine ait giriş kaydı YOK.
 *  - Sadece durum 'planli' veya 'devamsiz' olanlar aday (BOSS kararı verilmişse dokunulmaz).
 *  - Çalışılmış vardiyalar (giriş kaydı olan) KORUNUR → maaş için gerekli.
 * Döner: kaç vardiya iptal edildi, kaç çalışılmış vardiya korundu.
 */
export async function ayrilanPersonelVardiyalariniIptalEt(
  personelId: string
): Promise<{ iptalEdilen: number; korunan: number }> {
  const db = getFirestoreDb()

  // 1) Personelin tüm vardiyaları
  const vSnap = await getDocs(
    query(collection(db, VARDIYALAR), where('personelId', '==', personelId))
  )
  const vardiyalar = vSnap.docs.map((d) => ({ id: d.id, ...d.data() }) as Vardiya)

  // 2) Giriş kaydı olan tarihler (YYYY-MM-DD)
  const kSnap = await getDocs(
    query(collection(db, 'kayitlar'), where('personelId', '==', personelId))
  )
  const girisTarihleri = new Set<string>()
  kSnap.docs.forEach((d) => {
    const k = d.data() as { tip?: string; zaman?: { toDate: () => Date } }
    if (k.tip !== 'giris' || !k.zaman?.toDate) return
    const dt = k.zaman.toDate()
    const ds = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
    girisTarihleri.add(ds)
  })

  // 3) Aday: planli/devamsiz + BOSS kararı yok + o gün giriş yok → iptal
  const iptalAdaylari = vardiyalar.filter((v) => {
    const durumUygun = v.durum === 'planli' || v.durum === 'devamsiz'
    if (!durumUygun) return false
    if (v.devamsizKarari) return false
    return !girisTarihleri.has(v.tarih)
  })

  const korunan = vardiyalar.length - iptalAdaylari.length

  if (iptalAdaylari.length === 0) {
    return { iptalEdilen: 0, korunan }
  }

  // 4) Batch iptal (500 limiti — 400'lük gruplar)
  for (let i = 0; i < iptalAdaylari.length; i += 400) {
    const batch = writeBatch(db)
    iptalAdaylari.slice(i, i + 400).forEach((v) => {
      batch.update(doc(db, VARDIYALAR, v.id), {
        durum: 'iptal',
        guncellenmeTarihi: nowServer(),
      })
    })
    await batch.commit()
  }

  return { iptalEdilen: iptalAdaylari.length, korunan }
}

export async function haftayiYayinla(
  haftaBaslangic: string,
  subeId: string
): Promise<number> {
  const db = getFirestoreDb()
  const liste = await haftaVardiyalariGetir(haftaBaslangic, subeId)
  const taslaklar = liste.filter((v) => !v.yayinlandi)
  if (taslaklar.length === 0) return 0

  const batch = writeBatch(db)
  taslaklar.forEach((v) => {
    batch.update(doc(db, VARDIYALAR, v.id), {
      yayinlandi: true,
      guncellenmeTarihi: nowServer(),
    })
  })
  await batch.commit()

  // Yeni yayınlanan vardiyaların sahibi olan personellere sistem-içi bildirim.
  // Hata ana akışı bozmasın (görev modülü kalıbı: bildirim try/catch ile sarılı).
  try {
    const personelIdleri = Array.from(
      new Set(taslaklar.map((v) => v.personelId).filter(Boolean))
    )
    if (personelIdleri.length > 0) {
      const haftaEtiketi = haftaBaslangic.split('-').reverse().join('.')
      await cokluBildirimGonder(personelIdleri, {
        tipi: 'vardiyaYayinlandi',
        aciliyet: 'normal',
        baslik: '📅 Yeni vardiyaların hazır',
        mesaj: `${haftaEtiketi} haftası için vardiyaların yayınlandı.`,
        link: '/personel',
      })
    }
  } catch (e) {
    console.warn('[vardiya] yayınlanma bildirimi gönderilemedi:', e)
  }

  return taslaklar.length
}

export async function haftayiKopyala(
  kaynakHaftaBaslangic: string,
  hedefHaftaBaslangic: string,
  subeId: string,
  olusturanId: string
): Promise<number> {
  const kaynak = await haftaVardiyalariGetir(kaynakHaftaBaslangic, subeId)
  const hedef = await haftaVardiyalariGetir(hedefHaftaBaslangic, subeId)

  if (hedef.length > 0) {
    throw new Error(
      `Hedef hafta dolu (${hedef.length} vardiya). Önce mevcut planı temizle.`
    )
  }

  if (kaynak.length === 0) {
    throw new Error('Kaynak haftada hiç vardiya yok.')
  }

  const db = getFirestoreDb()
  const batch = writeBatch(db)

  const kaynakD = new Date(kaynakHaftaBaslangic)
  const hedefD = new Date(hedefHaftaBaslangic)
  const gunFarki = Math.round(
    (hedefD.getTime() - kaynakD.getTime()) / (1000 * 60 * 60 * 24)
  )

  let sayim = 0
  for (const eski of kaynak) {
    if (eski.durum !== 'planli') continue

    const eskiTarih = new Date(eski.tarih)
    const yeniTarih = new Date(eskiTarih)
    yeniTarih.setDate(yeniTarih.getDate() + gunFarki)

    const yeniDocId = doc(collection(db, VARDIYALAR)).id
    const yeniData: Record<string, unknown> = {
      personelId: eski.personelId,
      subeId: eski.subeId,
      tarih: tarihStr(yeniTarih),
      baslangic: eski.baslangic,
      bitis: eski.bitis,
      durum: 'planli',
      haftaBaslangic: hedefHaftaBaslangic,
      yayinlandi: false,
      olusturanId,
      olusturmaTarihi: nowServer(),
      guncellenmeTarihi: nowServer(),
    }
    if (eski.kalipId) yeniData.kalipId = eski.kalipId
    if (eski.pozisyonId) yeniData.pozisyonId = eski.pozisyonId

    batch.set(doc(db, VARDIYALAR, yeniDocId), yeniData)
    sayim++
  }

  await batch.commit()
  return sayim
}

export async function haftayiTemizle(
  haftaBaslangic: string,
  subeId: string
): Promise<number> {
  const db = getFirestoreDb()
  const liste = await haftaVardiyalariGetir(haftaBaslangic, subeId)
  if (liste.length === 0) return 0

  const batch = writeBatch(db)
  liste.forEach((v) => {
    batch.delete(doc(db, VARDIYALAR, v.id))
  })
  await batch.commit()
  return liste.length
}
